import { ProductIdentity, StatusBadge } from '@/components/products';
import { RAGBadge, RAGStatus } from '@/components/machines/RAGBadge';
import { isCatalogProductActive } from '@/lib/productCatalog';
import { calculateSlotRagStatus, type RagThresholds } from '@/lib/rag';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { SlotGridSlot } from './SlotGrid';

const UNASSIGNED_KEY = '__unassigned__';
const UNASSIGNED_STOCK_KEY = '__unassigned_stock__';

const RAG_SORT_RANK: Record<RAGStatus, number> = {
  RED: 0,
  AMBER: 1,
  GREEN: 2,
};

export interface ProductSlotGroup {
  key: string;
  productId: string | null;
  productName: string;
  product: SlotGridSlot['product'];
  slots: SlotGridSlot[];
  currentQuantity: number;
  maxCapacity: number;
  ragStatus: { status: RAGStatus; percentageFilled: number };
}

function slotLabel(slot: SlotGridSlot): string {
  return slot.slotLabel?.trim() || `#${slot.slotNumber}`;
}

function groupKeyForSlot(slot: SlotGridSlot): string {
  if (!slot.productId && slot.currentQuantity > 0) return UNASSIGNED_STOCK_KEY;
  if (!slot.productId) return UNASSIGNED_KEY;
  return slot.productId;
}

export function groupSlotsByProduct(
  slots: SlotGridSlot[],
  thresholds: RagThresholds
): ProductSlotGroup[] {
  const map = new Map<string, ProductSlotGroup>();

  for (const slot of slots) {
    const key = groupKeyForSlot(slot);
    const existing = map.get(key);

    if (existing) {
      existing.slots.push(slot);
      existing.currentQuantity += slot.currentQuantity;
      existing.maxCapacity += slot.maxCapacity;
    } else {
      const productName =
        key === UNASSIGNED_STOCK_KEY
          ? 'Stock without product link'
          : key === UNASSIGNED_KEY
            ? 'Empty slot'
            : slot.productName?.trim() ||
              slot.product?.name?.trim() ||
              slot.product?.sku?.trim() ||
              'Unknown product';

      map.set(key, {
        key,
        productId: slot.productId,
        productName,
        product: slot.product ?? null,
        slots: [slot],
        currentQuantity: slot.currentQuantity,
        maxCapacity: slot.maxCapacity,
        ragStatus: { status: 'GREEN', percentageFilled: 0 },
      });
    }
  }

  const groups = Array.from(map.values()).map((group) => ({
    ...group,
    slots: [...group.slots].sort((a, b) => slotLabel(a).localeCompare(slotLabel(b), undefined, { numeric: true })),
    ragStatus: calculateSlotRagStatus(group.currentQuantity, group.maxCapacity, thresholds),
  }));

  groups.sort((a, b) => {
    const rankA = sortRank(a);
    const rankB = sortRank(b);
    if (rankA !== rankB) return rankA - rankB;
    if (a.ragStatus.percentageFilled !== b.ragStatus.percentageFilled) {
      return a.ragStatus.percentageFilled - b.ragStatus.percentageFilled;
    }
    return a.productName.localeCompare(b.productName);
  });

  return groups;
}

function sortRank(group: ProductSlotGroup): number {
  if (group.key === UNASSIGNED_KEY && group.currentQuantity === 0) return 100;
  if (group.key === UNASSIGNED_STOCK_KEY) return 0;
  return RAG_SORT_RANK[group.ragStatus.status];
}

const slotPillClass: Record<RAGStatus, string> = {
  GREEN: 'bg-rag-good text-white border-transparent hover:bg-rag-good/90',
  AMBER: 'bg-rag-warning text-white border-transparent hover:bg-rag-warning/90',
  RED: 'bg-rag-critical text-white border-transparent hover:bg-rag-critical/90',
};

const slotPillAriaLabel: Record<RAGStatus, string> = {
  GREEN: 'Good stock',
  AMBER: 'Low stock',
  RED: 'Needs refill',
};

function SlotRagPill({ slot }: { slot: SlotGridSlot }) {
  const isEmpty = !slot.productId && slot.currentQuantity === 0;
  const status = slot.ragStatus.status;

  return (
    <Badge
      variant="outline"
      className={cn(
        'font-mono text-xs tabular-nums font-medium',
        isEmpty
          ? 'border-border bg-muted text-muted-foreground'
          : slotPillClass[status]
      )}
      aria-label={
        isEmpty
          ? `Slot ${slotLabel(slot)}: empty`
          : `Slot ${slotLabel(slot)}: ${slotPillAriaLabel[status]}`
      }
    >
      {slotLabel(slot)}
    </Badge>
  );
}

function StockLevelBar({ percentage, status }: { percentage: number; status: RAGStatus }) {
  const fillClass = {
    GREEN: 'bg-rag-good',
    AMBER: 'bg-rag-warning',
    RED: 'bg-rag-critical',
  }[status];

  return (
    <div className="flex min-w-[100px] items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-all duration-300', fillClass)}
          style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
        />
      </div>
      <span className="w-9 shrink-0 text-right font-mono text-xs tabular-nums text-muted-foreground">
        {Math.round(percentage)}%
      </span>
    </div>
  );
}

interface SlotProductListProps {
  slots: SlotGridSlot[];
  thresholds?: RagThresholds;
}

export function SlotProductList({
  slots,
  thresholds = { greenThreshold: 80, amberThreshold: 60 },
}: SlotProductListProps) {
  const groups = groupSlotsByProduct(slots, thresholds);

  if (groups.length === 0) {
    return null;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="min-w-[16rem] pl-5">Product</TableHead>
          <TableHead className="hidden sm:table-cell">Slots</TableHead>
          <TableHead className="w-28">Stock</TableHead>
          <TableHead className="min-w-[120px]">Fill level</TableHead>
          <TableHead className="w-16 pr-5 text-right">Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {groups.map((group) => (
          <TableRow key={group.key}>
            <TableCell className="pl-5">
              {group.productId ? (
                <div className="flex flex-col gap-1">
                  <ProductIdentity
                    productId={
                      isCatalogProductActive(group.product) ? group.productId : undefined
                    }
                    name={group.productName}
                    sku={group.product?.sku}
                    product={group.product}
                    size="sm"
                    showSku={false}
                  />
                  {!isCatalogProductActive(group.product) ? (
                    <StatusBadge isActive={false} />
                  ) : null}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <span className="text-xs font-medium">—</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-snug">{group.productName}</p>
                    {group.key === UNASSIGNED_STOCK_KEY ? (
                      <p className="text-xs text-muted-foreground">
                        Assign a catalog product to include in picklists
                      </p>
                    ) : null}
                  </div>
                </div>
              )}
            </TableCell>
            <TableCell className="hidden sm:table-cell">
              <div className="flex flex-wrap gap-1">
                {group.slots.map((slot) => (
                  <SlotRagPill key={slot.id} slot={slot} />
                ))}
              </div>
            </TableCell>
            <TableCell className="font-mono text-sm tabular-nums">
              {group.currentQuantity}/{group.maxCapacity}
            </TableCell>
            <TableCell>
              <StockLevelBar
                percentage={group.ragStatus.percentageFilled}
                status={group.ragStatus.status}
              />
            </TableCell>
            <TableCell className="pr-5 text-right">
              <RAGBadge status={group.ragStatus.status} variant="dot" size="sm" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default SlotProductList;
